import { useMemo } from 'react'
import { CreditCard, DollarSign, Cpu, ArrowDownRight, ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useDemoContext } from '../DemoContext'
import { useDemoData } from '../useDemoData'
import { formatDisplayedUsd } from '../../costDisplay'

const formatNumber = (n: number) => n.toLocaleString()

const formatDateTime = (value?: string | null, locale = 'en') => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function DemoCosts() {
  const { activeRole } = useDemoContext()
  const { t, i18n } = useTranslation('pages')

  const { wallet, transactions, costRows } = useDemoData()

  const totalCost = useMemo(
    () => costRows.reduce((sum, r) => sum + Number(r.cost_usd || 0), 0),
    [costRows],
  )

  const perSession = useMemo(() => {
    const map = new Map<number, { sessionName: string; cost: number }>()
    for (const row of costRows) {
      const current = map.get(row.session_id) || { sessionName: row.session_name, cost: 0 }
      current.cost += Number(row.cost_usd || 0)
      map.set(row.session_id, current)
    }
    return Array.from(map.entries()).map(([sessionId, v]) => ({
      session_id: sessionId,
      session_name: v.sessionName,
      cost_usd: v.cost,
    }))
  }, [costRows])

  /** Role-aware cost display */
  const displayCost = (rawUsd: number): string => {
    return formatDisplayedUsd(rawUsd)
  }

  const costColumnLabel = activeRole === 'ADMIN' ? t('common.cost') : t('costBreakdown.creditsUnit2')

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">{t('demo.costs.title')}</h2>
        <p className="page-header-sub">
          {activeRole === 'ADMIN'
            ? t('demo.costs.subtitleAdmin')
            : t('demo.costs.subtitleOwner')}
        </p>
      </div>

      {/* Credits balance */}
      <div className="card card-padding mb-24" style={{ maxWidth: 560 }}>
        <div className="flex items-center gap-8 mb-20">
          <CreditCard size={18} style={{ color: 'var(--accent-copper)' }} />
          <h3 className="card-title">{t('demo.costs.creditsCard')}</h3>
        </div>

        <div className="stat-card mb-20">
          <div className="stat-card-value">{wallet.read_balance.toFixed(1)}</div>
          <div className="stat-card-label">{t('demo.costs.remainingLabel')}</div>
        </div>

        {activeRole === 'ORG_MANAGER' && (
          <p className="text-sm text-muted">
            {t('demo.costs.subtitleManager')}
          </p>
        )}

        {activeRole === 'ADMIN' && (
          <p className="text-sm text-muted">
            {t('demo.costs.subtitleAdmin')}
          </p>
        )}

        <h4 className="text-sm font-semibold mb-12" style={{ marginTop: 20 }}>{t('demo.costs.transactionsSection')}</h4>
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>{t('demo.costs.colTransactionNo')}</th>
                <th>{t('demo.costs.colType')}</th>
                <th style={{ textAlign: 'right' }}>{t('demo.costs.colAmount')}</th>
                <th>{t('demo.costs.colDate')}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-muted text-sm">
                    {t('demo.costs.rowEmpty')}
                  </td>
                </tr>
              )}
              {transactions.map((tx) => (
                <tr key={tx.id}>
                  <td className="text-muted text-sm">#{tx.id}</td>
                  <td>
                    {(tx.credit_amount ?? 0) > 0 ? (
                      <span className="badge badge-success">
                        <ArrowDownRight size={12} />
                        {tx.type === 'ADJUST' ? t('demo.costs.editBadge') : t('demo.costs.uploadBadge')}
                      </span>
                    ) : (
                      <span className="badge badge-warning">
                        <ArrowUpRight size={12} />
                        {t('demo.costs.usageBadge')}
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span
                      className="font-heading font-semibold"
                      style={{
                        color:
                          (tx.credit_amount ?? 0) > 0 ? 'var(--color-success)' : 'var(--text-primary)',
                      }}
                    >
                      {(tx.credit_amount ?? 0) > 0 ? '+' : ''}
                      {(tx.credit_amount ?? 0).toFixed(1)}
                    </span>
                  </td>
                  <td className="text-muted text-sm">{formatDateTime(tx.created_at, i18n.language)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cost by Exam */}
      <div className="card card-padding mb-24">
        <div className="flex items-center gap-8 mb-16">
          <DollarSign size={18} style={{ color: 'var(--accent-copper)' }} />
          <h3 className="card-title">{t('demo.costs.usageByExam', { label: costColumnLabel })}</h3>
          <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
            {t('demo.costs.totalLabel')}{' '}
            <span
              className="font-heading font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              {displayCost(totalCost)}
            </span>
          </span>
        </div>
        {perSession.length === 0 ? (
          <p className="text-sm text-muted">{t('demo.costs.usageEmpty')}</p>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('demo.costs.colExam')}</th>
                  <th style={{ textAlign: 'right' }}>{costColumnLabel}</th>
                </tr>
              </thead>
              <tbody>
                {perSession.map((item) => (
                  <tr key={item.session_id}>
                    <td className="font-semibold">{item.session_name}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="font-heading font-semibold">
                        {displayCost(item.cost_usd)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cost by Exam + Model — only for ADMIN */}
      {activeRole === 'ADMIN' && (
        <div className="card card-padding">
          <div className="flex items-center gap-8 mb-16">
            <Cpu size={18} style={{ color: 'var(--accent-copper)' }} />
            <h3 className="card-title">{t('demo.costs.detailTitle')}</h3>
          </div>
          {costRows.length > 0 ? (
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('demo.costs.colExam')}</th>
                    <th>{t('demo.costs.colProvider')}</th>
                    <th>{t('demo.costs.colModel')}</th>
                    <th style={{ textAlign: 'right' }}>{t('demo.costs.colCalls')}</th>
                    <th style={{ textAlign: 'right' }}>{t('demo.costs.colInputTokens')}</th>
                    <th style={{ textAlign: 'right' }}>{t('demo.costs.colOutputTokens')}</th>
                    <th style={{ textAlign: 'right' }}>{t('demo.costs.colCost')}</th>
                  </tr>
                </thead>
                <tbody>
                  {costRows.map((r) => (
                    <tr key={`${r.session_id}-${r.provider}-${r.model}`}>
                      <td className="font-semibold">{r.session_name}</td>
                      <td>
                        <span className="badge badge-neutral">{r.provider || '-'}</span>
                      </td>
                      <td className="text-sm text-muted">{r.model || '-'}</td>
                      <td style={{ textAlign: 'right' }} className="text-sm">
                        {r.calls}
                      </td>
                      <td style={{ textAlign: 'right' }} className="text-sm">
                        {formatNumber(r.input_tokens)}
                      </td>
                      <td style={{ textAlign: 'right' }} className="text-sm">
                        {formatNumber(r.output_tokens)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="font-heading font-semibold">
                          {formatDisplayedUsd(Number(r.cost_usd || 0))}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted">{t('demo.costs.detailEmpty')}</p>
          )}
        </div>
      )}
    </div>
  )
}
